import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Col, Form } from "react-bootstrap";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  translationConstants,
  viewPathConstants,
  spotConstants,
  GoogleMapConstants,
  Directions,
  getNameById,
  defaultDropDownValue,
  globalConstants,
  defaultCountry,
} from "../../../../constants";
import GoogleMapView from "../../../../shared/components/GoogleMap/GoogleMapView";
import { centerListingActions, authActions } from "../../../../redux/actions";
import ViewCenterCard from "../../../../shared/components/ViewCenterCard/ViewCenterCard";
import {
  loadingIndicator,
  dataEndMessage,
} from "../../../../shared/utilitiies";
import { googleMapService } from "../../../../services";
import useFetchCoordinatesByCity from "../../../../shared/hooks/useFetchCordinatesByCity";
import { debounce } from "../../../../system/utility-functions";
//-------default query expression
const defaultQueryExpression = "id != null ";
const column = {
  cityId: "cityId",
};

//-------initial gridstate
const initialGridState = {
  length: 7,
  search: null,
  pageNumber: 1,
  orderBy: "id desc",
  filterQuery: defaultQueryExpression,
};

const ViewCenters = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  //-------reducers
  const centerListingReducer = useSelector(
    (state) => state.centerListingReducer
  );
  const direction = useSelector(
    (state) => state.LanguageChangeReducer.direction
  );
  const cities = useSelector((state) => state.LoginReducer.cities);
  const currentCity = useSelector((state) => state.GoogleMapReducer.city);
  //-------destructure reducers
  const { centerListingData } = centerListingReducer;

  //-------Center Listing
  const { pageData } = centerListingData;

  //-------UseState
  const [hasMore, setHasMore] = useState(true);
  const [centersArray, setCentersArray] = useState([]);
  const [gridState, setGridState] = useState(initialGridState);
  const [centersSpots, setCentersSpots] = useState([]);
  const [cityFilter, setCityFilter] = useState(defaultDropDownValue);
  const [currentCityId, setCurrentCityId] = useState(defaultDropDownValue);
  const hasMounted = useRef(false);
  const { location, mapCenter } = useFetchCoordinatesByCity(
    cityFilter,
    cities,
    centersSpots,
    pageData
  );
  //-------Map Style
  const MapContainerStyle = {
    width: "100%",
    height: "35vh",
    borderRadius: ".5em",
  };

  //-------Map options
  const options = {
    zoomControl: true,
    clickableIcons: false,
    disableDefaultUI: true,
    gestureHandling: "greedy",
  };

  //-------handle click on center card view details
  const handleViewDetails = (centerId) => {
    navigate(viewPathConstants.VIEW_CENTER_PROFILE, {
      state: {
        centerId: centerId,
      },
    });
  };

  //-------Get More Centers List on Scroll
  const getMoreCenterDetails = () => {
    const updatedGridState = { ...gridState, length: gridState.length + 7 };
    setGridState(updatedGridState);
  };

  const onFilterChange = useCallback(
    (key, value) => {
      let filterQueryExpression = defaultQueryExpression;

      filterQueryExpression += value ? ` && ${column.cityId} == ${value}` : "";

      setCityFilter(value);

      setGridState({
        ...gridState,
        filterQuery: filterQueryExpression,
      });
    },
    // eslint-disable-next-line
    [cityFilter]
  );

  const handleClearFilters = () => {
    setCityFilter("");
    setGridState({
      ...gridState,
      filterQuery: defaultQueryExpression,
    });
  };
  useEffect(() => {
    const isCityInSelector = cities.find((city) =>
      currentCity.includes(city.name)
    );
    if (isCityInSelector) {
      setCityFilter(isCityInSelector?.id);
      setCurrentCityId(isCityInSelector?.id);
    } else {
      setCityFilter(defaultCountry);
    }
  }, [currentCity, cities]);
  //-------UseEffects
  useEffect(() => {
    if (hasMounted.current) {
      dispatch(
        centerListingActions.getAllApprovedCentersList({
          ...gridState,
          filterQuery:
            (currentCityId !== defaultDropDownValue &&
              cityFilter == currentCityId) ||
            cityFilter === defaultCountry
              ? gridState.filterQuery + ` && ${column.cityId} == ${cityFilter}`
              : gridState.filterQuery,
        })
      );
    } else {
      hasMounted.current = true;
    }

    // eslint-disable-next-line
  }, [gridState, cityFilter, currentCityId]);

  useEffect(() => {
    let centersList = [];
    setCentersArray(pageData);

    pageData?.forEach((center, index) => {
      center?.centerSpots?.forEach((spot) => {
        const centerObj = {
          latitude: parseFloat(spot?.lat),
          longitude: parseFloat(spot?.long),
          locationName: spot?.spotName,
          locationArabicName: spot?.spotArabicName,
          centerName: center.name,
          centerArabicName: center.arabicName,
          sports: center.sports,
          isPublic: spot?.spotTypeId,
        };
        centersList.push(centerObj);
      });
    });

    setCentersSpots(centersList);
    // eslint-disable-next-line
  }, [pageData]);

  useEffect(() => {
    dispatch(authActions.fetchCities(process.env.REACT_APP_Country_Id));
    // eslint-disable-next-line
  }, []);

  //-------return view centers component
  return (
    <div className="container UserDiv pe-4">
      <div className="row py-2 pb-0 m-0">
        <div className="col-12 col-md-6">
          <p className="page-heading">{t(translationConstants.CENTERS)}</p>
        </div>
        <div className="col-12 col-md-6 p-0 d-md-flex justify-content-between justify-content-md-end">
          <div className="col-12 col-md-6 py-2 px-2">
            <Form.Select
              className="custom-select-icon filter-style"
              aria-label="Default select example"
              value={cityFilter}
              onChange={(event) =>
                onFilterChange(column.cityId, event.target.value)
              }
            >
              <option value="">
                {t(translationConstants.PLEASE_SELECT_A_CITY)}
              </option>
              {cities?.map((city) => (
                <option key={city.id} value={city.id}>
                  {direction === Directions.rtl ? city.arabicName : city.name}
                </option>
              ))}
            </Form.Select>
          </div>
          <div
            className={`${
              cityFilter
                ? "col-12 col-md-4 col-lg-2 d-md-flex justify-content-md-end w-auto"
                : "d-none"
            }`}
          >
            <div className="d-flex justify-content-md-end justify-content-center align-items-center mx-3">
              {cityFilter && (
                <div className="cancel-filter" onClick={handleClearFilters}>
                  {t(translationConstants.CLEAR_FILTERS)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="col-12 mt-3">
        <GoogleMapView
          containerStyle={MapContainerStyle}
          options={options}
          mapCenter={mapCenter}
          locations={
            centersSpots?.length === 0 && location != null
              ? [{ ...location, isShowMark: false }]
              : centersSpots
          }
        />
      </div>
      <div className="col-12 mt-3">
        <InfiniteScroll
          dataLength={centersArray?.length ?? 0}
          next={getMoreCenterDetails}
          hasMore={
            centersArray?.length % 7 === 0 && centersArray?.length !== 0
              ? true
              : false
          }
          loader={loadingIndicator(t)}
          endMessage={dataEndMessage(t, centersArray)}
          style={{ overflow: "hidden" }}
          scrollableTarget={"main-container"}
        >
          <div className="row m-0 w-100">
            {centersArray?.map((center) => {
              return (
                <ViewCenterCard
                  center={center}
                  handleViewDetails={handleViewDetails}
                />
              );
            })}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default ViewCenters;
